import React, { useCallback, useEffect, useState } from "react";
import Handler from "../../Helpers/Handler";
import { formatDateTime } from "../../Helpers/Utils";
import { DetailCard } from "../../Components/Needed";
import { FaClipboardList } from "react-icons/fa";

// KYC log (KYC v2, Phase 5): every red outcome of a customer's KYC documents (blocked, rejected, on hold, expired) and
// every override or refused override, newest first, with the entire data it was decided on. Read only: there is
// no edit or delete, and entries are never removed. Access follows customer.view (the same as the customer page).
// The admin and staff panels use this same file; they differ only in `basePath`.

const DOCS = [
    { key: "aadhaar", label: "Aadhaar" },
    { key: "dl", label: "Driving licence" },
    { key: "voter", label: "Voter ID" },
    { key: "passport", label: "Passport" },
];
const EVENT_LABEL = {
    daily_limit_blocked: "Blocked: daily limit",
    duplicate: "Rejected: duplicate",
    provider_rejected: "Rejected: provider",
    provider_error: "Provider error",
    rule_rejected: "Rejected: rule",
    reviewer_rejected: "Rejected: reviewer",
    hold: "On hold",
    expired: "Expired",
    override_reopened: "Override: sent back to review",
    override_kept: "Override: kept rejected",
    override_refused: "Override refused",
    attempts_reset: "Tries reset",
};
const EVENT_CLASS = {
    daily_limit_blocked: "secondary", duplicate: "danger", provider_rejected: "danger", provider_error: "secondary", rule_rejected: "danger",
    reviewer_rejected: "danger", hold: "warning text-dark", expired: "warning text-dark", override_reopened: "primary", override_kept: "dark", override_refused: "secondary", attempts_reset: "info text-dark",
};
const docLabel = (key) => DOCS.find((d) => d.key === key)?.label || key;

const KycLogPanel = ({ customerId, basePath = "/admin/customer", refreshKey = 0 }) => {
    const [entries, setEntries] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [documentType, setDocumentType] = useState("");
    const [event, setEvent] = useState("");
    const [detail, setDetail] = useState(null); // { logId, loading, entry }

    const load = useCallback(async () => {
        setLoading(true);
        const params = { limit: 100, ...(documentType ? { documentType } : {}), ...(event ? { event } : {}) };
        const response = await Handler({ method: "GET", url: `${basePath}/${customerId}/kyc/log`, params });
        const data = response?.success ? response?.data?.data || {} : {};
        setEntries(data.entries || []);
        if (data.events) setEvents(data.events);
        setLoading(false);
    }, [basePath, customerId, documentType, event]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const open = async (logId) => {
        if (detail?.logId === logId) { setDetail(null); return; }
        setDetail({ logId, loading: true });
        const response = await Handler({ method: "GET", url: `${basePath}/${customerId}/kyc/log/${logId}` });
        setDetail({ logId, loading: false, entry: response?.success ? response?.data?.data?.entry : null });
    };

    return (
        <DetailCard title="KYC log" icon={<FaClipboardList />}>
            <p className="small text-muted">Every blocked, rejected, held or expired document and every override, with the data it was decided on. Entries are never edited or deleted.</p>
            <div className="d-flex gap-2 flex-wrap mb-3">
                <select className="form-select form-select-sm w-auto" value={documentType} onChange={(e) => setDocumentType(e.target.value)} aria-label="Filter the KYC log by document">
                    <option value="">All documents</option>
                    {DOCS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                </select>
                <select className="form-select form-select-sm w-auto" value={event} onChange={(e) => setEvent(e.target.value)} aria-label="Filter the KYC log by event">
                    <option value="">All events</option>
                    {events.map((key) => <option key={key} value={key}>{EVENT_LABEL[key] || key}</option>)}
                </select>
            </div>
            {loading ? <div className="small text-muted">Loading...</div> : entries.length === 0 ? (
                <div className="small text-muted">Nothing logged for this customer.</div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                        <thead><tr><th>When</th><th>Event</th><th>Document</th><th>Reason</th><th>By</th><th /></tr></thead>
                        <tbody>
                            {entries.map((row) => (
                                <React.Fragment key={row.logId}>
                                    <tr>
                                        <td className="small text-nowrap">{formatDateTime(row.createdAt)}</td>
                                        <td><span className={`badge bg-${EVENT_CLASS[row.event] || "secondary"}`}>{EVENT_LABEL[row.event] || row.event}</span></td>
                                        <td className="small">{docLabel(row.documentType)}</td>
                                        <td className="small">{row.reasonText || row.reasonCode || "-"}{row.rule?.name ? <div className="text-muted">{row.rule.name}: {String(row.rule.actual ?? "")}{row.rule.expected !== undefined ? ` (needs ${row.rule.expected})` : ""}</div> : null}</td>
                                        <td className="small text-capitalize">{row.actor?.role || "-"}{row.actor?.id ? <span className="text-muted text-lowercase"> {row.actor.id}</span> : null}</td>
                                        <td className="text-end"><button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => open(row.logId)}>{detail?.logId === row.logId ? "Hide" : "Details"}</button></td>
                                    </tr>
                                    {detail?.logId === row.logId && (
                                        <tr><td colSpan={6}>
                                            {detail.loading ? <div className="small text-muted">Loading...</div> : !detail.entry ? <div className="small text-danger">Could not load this entry.</div> : (
                                                <div className="border rounded-3 p-2 bg-light small">
                                                    {detail.entry.overrideReason ? <div className="mb-1"><strong>Override reason:</strong> {detail.entry.overrideReason}{detail.entry.proofUrl ? <> (<a href={detail.entry.proofUrl} target="_blank" rel="noopener noreferrer">proof</a>)</> : null}</div> : null}
                                                    <div className="text-muted mb-1">{detail.entry.logId}{detail.entry.attemptId ? ` · attempt ${detail.entry.attemptId}` : ""}{detail.entry.source ? ` · from ${detail.entry.source}` : ""}</div>
                                                    <pre className="mb-0" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 320, overflow: "auto" }}>{JSON.stringify({ rule: detail.entry.rule, data: detail.entry.data, provider: detail.entry.provider }, null, 2)}</pre>
                                                </div>
                                            )}
                                        </td></tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </DetailCard>
    );
};

export default KycLogPanel;
